import {ActionList, ActionMenu} from '@primer/react'
import {useCategory} from '@github-ui/marketplace-common/CategoryContext'
import {useModelsTask} from '@github-ui/marketplace-common/ModelsTaskContext'
import {
  allCategoriesOptionID,
  allPublishersOption,
  getCategoryDisplayName,
  taskOptions,
  selectedTaskName,
} from '@github-ui/marketplace-common/model-filter-options'
import {ModelsSortMenu} from './ModelsSortMenu'
import {useModelsPublisher} from '@github-ui/marketplace-common/ModelsPublisherContext'
import {usePage} from '@github-ui/marketplace-common/PageContext'
import {testIdProps} from '@github-ui/test-id-props'
import {useModelsSearchFilters} from '@github-ui/marketplace-common/use-models-search-filters'

export default function ModelsFilters() {
  const {publisher, publisherDisplayName, publishers, setPublisher} = useModelsPublisher()
  const {task, setTask} = useModelsTask()
  const {category, setCategory} = useCategory()
  const {setPage} = usePage()
  const {
    data: {categories},
  } = useModelsSearchFilters()

  const onTaskChange = (newTask: string) => {
    setPage(1)
    setTask(newTask)
  }

  const onPublisherChange = (newPublisher: string) => {
    setPage(1)
    setPublisher(newPublisher)
  }

  const onCategoryChange = (newCategory: string) => {
    setPage(1)
    setCategory(newCategory)
  }

  const categoryName = getCategoryDisplayName(categories.find(categoryID => categoryID === category))

  return (
    <div className="d-flex gap-2 flex-wrap">
      <ActionMenu>
        <ActionMenu.Button {...testIdProps('family-button')}>
          <span className="fgColor-muted">Publisher:</span> {publisherDisplayName ?? publisher}
        </ActionMenu.Button>
        <ActionMenu.Overlay width="small">
          <ActionList selectionVariant="single">
            <ActionList.Item
              key={allPublishersOption}
              selected={allPublishersOption === publisher || !publisher}
              onSelect={() => onPublisherChange(allPublishersOption)}
            >
              All
            </ActionList.Item>
            {publishers.map(({displayName, name}) => (
              <ActionList.Item
                key={name}
                selected={name.toLowerCase() === publisher?.toLowerCase()}
                onSelect={() => onPublisherChange(name)}
              >
                {displayName ?? name}
              </ActionList.Item>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <ActionMenu>
        <ActionMenu.Button data-testid="task-button">
          <span className="fgColor-muted">Capability:</span> {selectedTaskName(task)}
        </ActionMenu.Button>
        <ActionMenu.Overlay width="small">
          <ActionList selectionVariant="single">
            {taskOptions.map(({id, name}) => (
              <ActionList.Item key={id} selected={id === task} onSelect={() => onTaskChange(id)}>
                {name}
              </ActionList.Item>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <ActionMenu>
        <ActionMenu.Button data-testid="category-button">
          <span className="fgColor-muted">Category:</span> {categoryName}
        </ActionMenu.Button>
        <ActionMenu.Overlay width="small">
          <ActionList selectionVariant="single">
            <ActionList.Item
              key={allCategoriesOptionID}
              selected={allCategoriesOptionID === category || !category}
              onSelect={() => onCategoryChange(allCategoriesOptionID)}
            >
              All
            </ActionList.Item>
            {categories.map(categoryID => (
              <ActionList.Item
                key={categoryID}
                // Default to "All" if no category is selected. We can't do this with default state
                // because `category` is shared between models and apps/actions
                selected={categoryID === category || (categoryID === allCategoriesOptionID && !category)}
                onSelect={() => onCategoryChange(categoryID)}
              >
                {getCategoryDisplayName(categoryID)}
              </ActionList.Item>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <ModelsSortMenu />
    </div>
  )
}
